import { useContext, useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { CurrencySymbol, TaxData } from '../../../types/location';
import { Button, ButtonVariant } from '../../elements/Button';
import { ThreeDotsIcon } from '../../elements/ThreeDotsIcon';
import XplNumberInputWithLabel from '../../elements/XplInputWithLabel';
import EditTaxRatesDropdown from '../../modals/EditTaxRatesDropdown';
import EditTaxRateModal from '../../modals/EditTaxRatesModal';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { EditLocationContext } from '../../../context/EditLocationContext';

interface TaxesFormTableProps {
  partnerId: string;
  taxRatesDataResponse: TaxData[];
}
const TaxesFormTable = ({
  partnerId,
  taxRatesDataResponse,
}: TaxesFormTableProps) => {
  const {
    register,
    unregister,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { currency } = useContext(EditLocationContext);

  const modalContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownOpenState, setDropdownOpenState] = useState<{
    [key: number]: boolean;
  }>({});

  const [openEditTaxRateModal, setOpenEditTaxRateModal] = useState<{
    [key: number]: TaxData;
  }>({});

  const toggleDropdown = (index: number) => {
    setDropdownOpenState((prevState) => ({
      [index]: !prevState[index],
    }));
  };
  const handleAddMinOrMaxPrice = (
    index: number,
    applicableKey: 'min_applicable' | 'max_applicable',
  ) => {
    const currentValue = watch(`data.${index}.${applicableKey}`);

    if (currentValue || currentValue === '') {
      unregister(`data.${index}.${applicableKey}`);
      setValue(`data.${index}.${applicableKey}`, undefined, {
        shouldDirty: true,
      });
    } else {
      setValue(`data.${index}.${applicableKey}`, '', { shouldDirty: true });
    }
  };

  const handleOpenEditTaxRateModal = () => {
    setDropdownOpenState({});
    setOpenEditTaxRateModal({});
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalContainerRef.current && event.target instanceof Node) {
        if (
          modalContainerRef.current &&
          !modalContainerRef.current.contains(event.target) &&
          !buttonRef.current?.contains(event.target)
        ) {
          setDropdownOpenState({});
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validatedErrors = errors as unknown as FieldErrors<{
    data: {
      tax_id: string;
      tax_rate: string;
      min_applicable: string;
      max_applicable: string;
    }[];
  }>;

  return (
    <TableContainer className='!overflow-visible'>
      <Table className='!w-full !min-w-[1225px]'>
        <TableHead>
          <TableRow className='gap-[74px] !pl-0'>
            <TableCell className='min-w-[200px] !pl-0'>
              <p className='font-bold'>Tax Rate Type</p>
            </TableCell>
            <TableCell className='w-[200px]'>
              <p className='font-bold'>Percentage</p>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {taxRatesDataResponse?.map((tax, index) => {
            const currentMinPrice = watch(`data.${index}.min_applicable`);
            const currentMaxPrice = watch(`data.${index}.max_applicable`);
            const minPriceExisting = currentMinPrice !== undefined;
            const maxPriceExisting = currentMaxPrice !== undefined;

            return (
              <TableRow
                key={index}
                className='pl-0 border-t border-b border-gray-300 '
              >
                <TableCell
                  className='h-full !pl-0 w-[340px]'
                  data-test-id={tax.tax_rate_name}
                >
                  {tax.tax_rate_name}
                </TableCell>

                <TableCell className='!pb-0'>
                  <XplNumberInputWithLabel
                    inlinelabel='%'
                    data-test-id={`${tax?.product_class_name}${index}`}
                    {...register(`data.${index}.tax_rate`)}
                    error={validatedErrors?.data?.[index]?.tax_rate?.message}
                  />
                </TableCell>

                {minPriceExisting && (
                  <TableCell className='w-[200px] !p-0 !pl-4 !pr-4'>
                    <XplNumberInputWithLabel
                      label='Min Price'
                      currency={currency ? CurrencySymbol[currency] : '$'}
                      inlinelabel={currency ?? 'USD'}
                      {...register(`data.${index}.min_applicable`)}
                      error={
                        validatedErrors?.data?.[index]?.min_applicable?.message
                      }
                    />
                  </TableCell>
                )}

                {maxPriceExisting && (
                  <TableCell className='w-[200px] !p-0 !pl-4 !pr-4'>
                    <XplNumberInputWithLabel
                      label='Max Price'
                      currency={currency ? CurrencySymbol[currency] : '$'}
                      inlinelabel={currency ?? 'USD'}
                      {...register(`data.${index}.max_applicable`)}
                      error={
                        validatedErrors?.data?.[index]?.max_applicable?.message
                      }
                    />
                  </TableCell>
                )}

                <TableCell className='relative'>
                  <div ref={buttonRef}>
                    <Button
                      variant={ButtonVariant.Default}
                      onClick={() => {
                        toggleDropdown(index);
                      }}
                      icon={<ThreeDotsIcon />}
                      dataTestId={`three_dots_button_${index}`}
                      className='w-10 h-10 rounded-full flex bg-white justify-center items-center'
                    />
                  </div>

                  {dropdownOpenState[index] && (
                    <div
                      ref={modalContainerRef}
                      className={`absolute left-12 top-3 z-50 bg-white`}
                    >
                      <EditTaxRatesDropdown
                        handleAddMinOrMaxPrice={handleAddMinOrMaxPrice}
                        setModalOpen={setDropdownOpenState}
                        setOpenEditModal={setOpenEditTaxRateModal}
                        index={index}
                        tax={tax}
                        partnerId={partnerId}
                        minPrice={minPriceExisting}
                        maxPrice={maxPriceExisting}
                      />
                    </div>
                  )}
                </TableCell>

                {openEditTaxRateModal[index] && (
                  <EditTaxRateModal
                    handleOpenEditTaxRateModal={handleOpenEditTaxRateModal}
                    taxData={openEditTaxRateModal[index]}
                    partnerId={partnerId}
                  />
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TaxesFormTable;
